import clsx from "clsx";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { UserUpdate, useUpdateUser } from "@/hooks/api/user/useUpdateUser";
import Button from "@/components/atoms/Button";
import FormInput from "@/components/molecules/form/FormInput";
import StepHeader from "@/components/organisms/onboarding/OnboardingStepHeader";

type Props = {
  handleBack: () => void;
  handleNext: (formData: UserUpdate) => void;
  step: number;
  maxSteps: number;
};

const OnboardingUser = ({ handleBack, handleNext, step, maxSteps }: Props) => {
  const { t } = useTranslation();
  const { updateUserAsync } = useUpdateUser();
  const methods = useForm<UserUpdate>({
    defaultValues: {
      firstName: "",
      lastName: "",
      position: "",
    },
  });

  const onSubmit: SubmitHandler<UserUpdate> = async (data) => {
    await updateUserAsync(data);
    handleNext(data);
  };

  return (
    <>
      <StepHeader
        heading={t("onboarding.steps.user.title")}
        description={t("onboarding.steps.user.description")}
        step={step}
        maxSteps={maxSteps}
      />
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} className={"flex h-full flex-col"}>
          <div className={"flex flex-1 flex-col gap-8"}>
            <FormInput
              name={"firstName"}
              label={t("onboarding.fields.firstName")}
              placeholder={t("onboarding.fields.firstName")}
              rules={{ required: t("validation.requiredName", { name: t("onboarding.fields.firstName") }) }}
              sizing={"xs"}
            />
            <FormInput
              name={"lastName"}
              label={t("onboarding.fields.lastName")}
              placeholder={t("onboarding.fields.lastName")}
              rules={{ required: t("validation.requiredName", { name: t("onboarding.fields.lastName") }) }}
              sizing={"xs"}
            />
            <FormInput
              name={"position"}
              label={t("onboarding.fields.position")}
              placeholder={t("onboarding.fields.positionPlaceholder")}
              rules={{ required: t("validation.requiredName", { name: t("onboarding.fields.position") }) }}
              sizing={"xs"}
            />
          </div>

          <div className={"grid grid-cols-4 gap-4"}>
            {step > 1 && (
              <Button
                type="button"
                onClick={handleBack}
                label={t("onboarding.control.back")}
                className={"col-span-2"}
                color={"natural"}
                variants={"outline"}
              />
            )}
            <Button
              type="submit"
              className={clsx("col-span-2", step === 1 && "col-start-2")}
              label={step !== maxSteps ? t("onboarding.control.next") : t("onboarding.control.finish")}
            />
          </div>
        </form>
      </FormProvider>
    </>
  );
};

export default OnboardingUser;
